import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import type { AdAudienceResult } from '@/services/auth/types';
import { COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import { filterAudienceCredentials, mapAudienceCredentialToSearchResult } from './utils/integration-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseAdAudiencesSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type AdAudiencesSource = SearchSource<AdAudienceResult>;

export function useAdAudiencesSource({ workspaceId, enabled, query }: UseAdAudiencesSourceOptions): AdAudiencesSource {
  const adAudiencesQuery = useQuery(
    getHydraUIAPIQueryOptions({
      queryKey: ['getAudienceCredentials', { params: { environment_id: workspaceId } }],
      queryFn: ({ signal }) =>
        hydraUiApi.audienceCredentials.list({
          params: { path: { environment_id: String(workspaceId) } },
          signal,
        }),
      queryConfig: {
        enabled,
        ...COMMAND_DIALOG_QUERY_DEFAULTS,
        select: (data) => data.audience_credentials,
      },
    }),
  );

  const results = useMemo(
    () =>
      filterAudienceCredentials(adAudiencesQuery.data ?? [], query).map((credential) =>
        mapAudienceCredentialToSearchResult(credential, workspaceId),
      ),
    [adAudiencesQuery.data, query, workspaceId],
  );

  return { ...adAudiencesQuery, results };
}
